import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Sprout, Plus, Trash2, ArrowUp, ArrowDown, Pencil } from 'lucide-react';
import nurseryPropagationService from '../../services/nurseryPropagation';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { overallLossPct } from '../../utils/propagation';
import { LOT_STAGE_LABELS } from '../../types/nursery';
import type { LotStage } from '../../types/nursery';
import {
  FAMILY_LOT_STAGES,
  PROPAGATION_FAMILIES,
  PROPAGATION_FAMILY_LABELS,
} from '../../types/nurseryPropagation';
import type {
  ContainerType,
  PropagationFamily,
  PropagationMethod,
  PropagationMethodPayload,
} from '../../types/nurseryPropagation';

/**
 * /nursery/propagation/setup — the organisation's propagation methods and
 * container types (propagation plan, Phase 0).
 *
 * A method is the organisation's own name for how it propagates ("Softwood
 * under mist", "Chip bud"), of one family, with the steps it goes through:
 * what each step loses and how long it takes. Every figure is typed by the
 * organisation; nothing is filled in for it (Rob, 2026-10-12). Later phases
 * plan production orders from these, and replace them with the
 * organisation's own measured rates.
 *
 * Anyone who records movements reads the page; the nursery admin edits it.
 */

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

type Tab = 'methods' | 'containers';

interface StageDraft {
  name: string;
  lot_stage: LotStage | '';
  expected_loss_pct: string;
  typical_weeks: string;
}

interface MethodDraft {
  id: number | null;
  name: string;
  family: PropagationFamily;
  wood_window_days: string;
  notes: string;
  stages: StageDraft[];
}

const numOrNull = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const asText = (v: unknown): string => (v == null ? '' : String(Number(v)));

function draftFrom(m: PropagationMethod | null): MethodDraft {
  if (!m) return { id: null, name: '', family: 'cutting', wood_window_days: '', notes: '', stages: [] };
  return {
    id: m.id,
    name: m.name,
    family: m.family,
    wood_window_days: m.wood_window_days == null ? '' : String(m.wood_window_days),
    notes: m.notes ?? '',
    stages: m.stages.map((s) => ({
      name: s.name,
      lot_stage: s.lot_stage ?? '',
      expected_loss_pct: asText(s.expected_loss_pct),
      typical_weeks: asText(s.typical_weeks),
    })),
  };
}

function MethodEditor({ draft, onChange, onCancel, onSave, saving, error }: {
  draft: MethodDraft;
  onChange: (d: MethodDraft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
}) {
  const stagesFor = FAMILY_LOT_STAGES[draft.family];
  const setStage = (i: number, patch: Partial<StageDraft>) =>
    onChange({ ...draft, stages: draft.stages.map((s, n) => (n === i ? { ...s, ...patch } : s)) });
  const move = (i: number, by: number) => {
    const next = [...draft.stages];
    const [s] = next.splice(i, 1);
    next.splice(i + by, 0, s);
    onChange({ ...draft, stages: next });
  };
  const usesWood = draft.family === 'bud' || draft.family === 'graft';

  return (
    <div className="bg-white border border-primary-200 rounded-xl p-4 space-y-4">
      <h3 className="text-base font-semibold text-neutral-800">{draft.id ? `Edit ${draft.name || 'method'}` : 'New method'}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="method-name" className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
          <input id="method-name" className={inputCls} maxLength={100} value={draft.name}
            placeholder="e.g. Softwood under mist"
            onChange={(e) => onChange({ ...draft, name: e.target.value })} />
        </div>
        <div>
          <label htmlFor="method-family" className="block text-sm font-medium text-neutral-700 mb-1">Kind of propagation</label>
          <select id="method-family" className={inputCls} value={draft.family} disabled={draft.id != null}
            onChange={(e) => {
              const family = e.target.value as PropagationFamily;
              const allowed = FAMILY_LOT_STAGES[family];
              onChange({
                ...draft, family,
                stages: draft.stages.map((s) => (s.lot_stage && !allowed.includes(s.lot_stage) ? { ...s, lot_stage: '' } : s)),
              });
            }}>
            {PROPAGATION_FAMILIES.map((f) => <option key={f} value={f}>{PROPAGATION_FAMILY_LABELS[f]}</option>)}
          </select>
          {draft.id != null && (
            <p className="text-xs text-neutral-500 mt-1">Fixed once saved: work has been recorded as this kind.</p>
          )}
        </div>
        {usesWood && (
          <div>
            <label htmlFor="method-wood" className="block text-sm font-medium text-neutral-700 mb-1">Wood stays usable (days)</label>
            <input id="method-wood" className={inputCls} type="text" inputMode="numeric" value={draft.wood_window_days}
              onChange={(e) => onChange({ ...draft, wood_window_days: e.target.value })} />
          </div>
        )}
        <div className="sm:col-span-2">
          <label htmlFor="method-notes" className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
          <textarea id="method-notes" className={inputCls} rows={2} value={draft.notes}
            onChange={(e) => onChange({ ...draft, notes: e.target.value })} />
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h4 className="text-sm font-semibold text-neutral-700">Steps</h4>
          <Button variant="outline" size="sm" onClick={() => onChange({
            ...draft, stages: [...draft.stages, { name: '', lot_stage: '', expected_loss_pct: '', typical_weeks: '' }],
          })}>
            <Plus className="h-4 w-4 mr-1" /> Add a step
          </Button>
        </div>
        <p className="text-xs text-neutral-500 mb-2">
          In order. The loss is the share you expect not to make it through the step; the weeks are how long it usually takes.
          Production orders plan from these until your own records replace them.
        </p>
        {draft.stages.length === 0 ? (
          <p className="text-sm text-neutral-500">No steps yet.</p>
        ) : (
          <ul className="space-y-2">
            {draft.stages.map((s, i) => (
              <li key={i} className="border border-neutral-200 rounded-lg p-3">
                <div className="grid grid-cols-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2">
                  <div className="col-span-2 sm:col-span-1">
                    <label htmlFor={`stage-name-${i}`} className="block text-xs text-neutral-500 mb-1">Step</label>
                    <input id={`stage-name-${i}`} className={inputCls} maxLength={100} value={s.name}
                      placeholder="e.g. Strike under mist" onChange={(e) => setStage(i, { name: e.target.value })} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label htmlFor={`stage-lot-${i}`} className="block text-xs text-neutral-500 mb-1">Ends with the lot</label>
                    <select id={`stage-lot-${i}`} className={inputCls} value={s.lot_stage}
                      onChange={(e) => setStage(i, { lot_stage: e.target.value as LotStage | '' })}>
                      <option value="">Not a lot stage</option>
                      {stagesFor.map((st) => <option key={st} value={st}>{LOT_STAGE_LABELS[st]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor={`stage-loss-${i}`} className="block text-xs text-neutral-500 mb-1">Loss %</label>
                    <input id={`stage-loss-${i}`} className={inputCls} type="text" inputMode="decimal" value={s.expected_loss_pct}
                      onChange={(e) => setStage(i, { expected_loss_pct: e.target.value })} />
                  </div>
                  <div>
                    <label htmlFor={`stage-weeks-${i}`} className="block text-xs text-neutral-500 mb-1">Weeks</label>
                    <input id={`stage-weeks-${i}`} className={inputCls} type="text" inputMode="decimal" value={s.typical_weeks}
                      onChange={(e) => setStage(i, { typical_weeks: e.target.value })} />
                  </div>
                </div>
                <div className="flex justify-end gap-1 mt-2">
                  <button type="button" aria-label="Move step up" disabled={i === 0} onClick={() => move(i, -1)}
                    className="p-1.5 rounded text-neutral-500 hover:bg-neutral-100 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" aria-label="Move step down" disabled={i === draft.stages.length - 1} onClick={() => move(i, 1)}
                    className="p-1.5 rounded text-neutral-500 hover:bg-neutral-100 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                  <button type="button" aria-label="Remove step"
                    onClick={() => onChange({ ...draft, stages: draft.stages.filter((_, n) => n !== i) })}
                    className="p-1.5 rounded text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save method'}</Button>
      </div>
    </div>
  );
}

function MethodCard({ method, canEdit, onEdit, onToggle }: {
  method: PropagationMethod; canEdit: boolean; onEdit: () => void; onToggle: () => void;
}) {
  const overall = overallLossPct(method.stages);
  const weeks = method.stages.reduce((n, s) => n + (s.typical_weeks == null ? 0 : Number(s.typical_weeks)), 0);
  return (
    <div className={`bg-white border rounded-xl p-4 ${method.active ? 'border-neutral-200' : 'border-dashed border-neutral-300 opacity-70'}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-neutral-800 break-words">{method.name}</h3>
          <p className="text-xs text-neutral-500">
            {PROPAGATION_FAMILY_LABELS[method.family]}
            {!method.active && ' · not in use'}
            {method.wood_window_days != null && ` · wood usable ${method.wood_window_days} days`}
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={onEdit}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
            <Button variant="outline" size="sm" onClick={onToggle}>{method.active ? 'Stop using' : 'Use again'}</Button>
          </div>
        )}
      </div>
      {method.notes && <p className="text-sm text-neutral-600 mt-2 break-words">{method.notes}</p>}
      {method.stages.length === 0 ? (
        <p className="text-sm text-amber-700 mt-3">No steps yet. Add them so production orders can be planned with this method.</p>
      ) : (
        <>
          <ol className="mt-3 space-y-1">
            {method.stages.map((s, i) => (
              <li key={s.id ?? i} className="flex flex-wrap items-baseline gap-x-3 text-sm">
                <span className="text-neutral-400 w-5">{i + 1}.</span>
                <span className="text-neutral-800 break-words min-w-0">{s.name}</span>
                {s.lot_stage && <span className="text-xs text-neutral-500">→ {LOT_STAGE_LABELS[s.lot_stage]}</span>}
                <span className="text-xs text-neutral-500">
                  {s.expected_loss_pct == null ? 'loss not set' : `${Number(s.expected_loss_pct)}% loss`}
                  {s.typical_weeks != null && ` · ${Number(s.typical_weeks)} wk`}
                </span>
              </li>
            ))}
          </ol>
          <p className="text-xs text-neutral-500 mt-2">
            {`${overall != null ? `About ${overall}% lost from start to finish` : 'Losses not set'}${weeks > 0 ? `, over about ${Math.round(weeks * 10) / 10} weeks` : ''}.`}
          </p>
        </>
      )}
    </div>
  );
}

function ContainerTypes({ items, canEdit, reload }: { items: ContainerType[]; canEdit: boolean; reload: () => void }) {
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [name, setName] = useState('');
  const [cells, setCells] = useState('');
  const [area, setArea] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = (c: ContainerType | null) => {
    setEditingId(c ? c.id : 'new');
    setName(c?.name ?? '');
    setCells(c ? String(c.cells_per_container) : '');
    setArea(c?.area_m2 == null ? '' : String(Number(c.area_m2)));
    setError(null);
  };

  const save = async () => {
    const cellsN = Number(cells);
    if (!name.trim() || !Number.isInteger(cellsN) || cellsN < 1) {
      setError('Give it a name and how many plants one holds.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = { name: name.trim(), cells_per_container: cellsN, area_m2: numOrNull(area) };
      const res = editingId === 'new'
        ? await nurseryPropagationService.createContainerType(payload)
        : await nurseryPropagationService.updateContainerType(Number(editingId), payload);
      if (!res.success) { setError(errorMessage(res, 'Could not save the container type')); return; }
      setEditingId(null);
      reload();
    } catch (e) {
      setError(errorMessage(e, 'Could not save the container type'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (c: ContainerType) => {
    try {
      await nurseryPropagationService.updateContainerType(c.id, { active: !c.active });
      reload();
    } catch (e) {
      setError(errorMessage(e, 'Could not change the container type'));
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600">
        Trays, cell flats and flasks. A batch in them is counted as containers × plants per container, or tray by tray.
      </p>
      {canEdit && editingId == null && (
        <Button variant="primary" size="sm" onClick={() => open(null)}><Plus className="h-4 w-4 mr-1" /> New container type</Button>
      )}
      {editingId != null && (
        <div className="bg-white border border-primary-200 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label htmlFor="container-name" className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
              <input id="container-name" className={inputCls} maxLength={100} value={name}
                placeholder="e.g. 35 mm Ellepot tray" onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label htmlFor="container-cells" className="block text-sm font-medium text-neutral-700 mb-1">Plants per container</label>
              <input id="container-cells" className={inputCls} type="text" inputMode="numeric" value={cells}
                onChange={(e) => setCells(e.target.value)} />
            </div>
            <div>
              <label htmlFor="container-area" className="block text-sm font-medium text-neutral-700 mb-1">Bench space (m²)</label>
              <input id="container-area" className={inputCls} type="text" inputMode="decimal" value={area}
                onChange={(e) => setArea(e.target.value)} />
            </div>
          </div>
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-sm text-neutral-500">No container types yet.</p>
      ) : (
        <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
          {items.map((c) => (
            <li key={c.id} className={`flex flex-wrap items-center justify-between gap-2 px-4 py-3 ${c.active ? '' : 'opacity-60'}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-neutral-800 break-words">{c.name}</p>
                <p className="text-xs text-neutral-500">
                  {c.cells_per_container} per container
                  {c.area_m2 != null && ` · ${Number(c.area_m2)} m²`}
                  {!c.active && ' · not in use'}
                </p>
              </div>
              {canEdit && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => open(c)}>Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => toggle(c)}>{c.active ? 'Stop using' : 'Use again'}</Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PropagationSetup() {
  const { hasAccess: isAdmin } = useModuleLicense('nursery', 'admin');
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'containers' ? 'containers' : 'methods';
  const [methods, setMethods] = useState<PropagationMethod[] | null>(null);
  const [containers, setContainers] = useState<ContainerType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<MethodDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [m, c] = await Promise.all([
        nurseryPropagationService.getMethods(true),
        nurseryPropagationService.getContainerTypes(true),
      ]);
      if (m.success) setMethods(m.data);
      else setError(errorMessage(m, 'Could not load the propagation methods'));
      if (c.success) setContainers(c.data);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the propagation set-up'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveMethod = async () => {
    if (!draft) return;
    if (!draft.name.trim()) { setSaveError('Give the method a name.'); return; }
    if (draft.stages.some((s) => !s.name.trim())) { setSaveError('Every step needs a name.'); return; }
    const bad = draft.stages.find((s) => {
      const loss = numOrNull(s.expected_loss_pct);
      return (s.expected_loss_pct.trim() !== '' && (loss == null || loss < 0 || loss >= 100))
        || (s.typical_weeks.trim() !== '' && (numOrNull(s.typical_weeks) == null || Number(s.typical_weeks) < 0));
    });
    if (bad) { setSaveError(`Check "${bad.name}": a loss is 0 to under 100, and weeks are 0 or more.`); return; }

    const payload: PropagationMethodPayload = {
      name: draft.name.trim(),
      wood_window_days: numOrNull(draft.wood_window_days),
      notes: draft.notes.trim() || null,
      stages: draft.stages.map((s) => ({
        name: s.name.trim(),
        lot_stage: s.lot_stage || null,
        expected_loss_pct: numOrNull(s.expected_loss_pct),
        typical_weeks: numOrNull(s.typical_weeks),
      })),
    };
    if (draft.id == null) payload.family = draft.family;
    setSaving(true);
    setSaveError(null);
    try {
      const res = draft.id == null
        ? await nurseryPropagationService.createMethod(payload)
        : await nurseryPropagationService.updateMethod(draft.id, payload);
      if (!res.success) { setSaveError(errorMessage(res, 'Could not save the method')); return; }
      setDraft(null);
      load();
    } catch (e) {
      setSaveError(errorMessage(e, 'Could not save the method'));
    } finally {
      setSaving(false);
    }
  };

  const toggleMethod = async (m: PropagationMethod) => {
    try {
      await nurseryPropagationService.updateMethod(m.id, { active: !m.active });
      load();
    } catch (e) {
      setError(errorMessage(e, 'Could not change the method'));
    }
  };

  const tabBtn = (t: Tab, label: string) => (
    <button
      type="button"
      aria-current={tab === t ? 'page' : undefined}
      onClick={() => setParams(t === 'methods' ? {} : { tab: t }, { replace: true })}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${tab === t ? 'bg-primary-600 text-white' : 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'}`}
    >
      {label}
    </button>
  );

  return (
    <>
      <PageHeader
        title="Propagation set-up"
        subtitle="Your methods, their steps and losses, and the trays they use"
        icon={<Sprout className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          {tabBtn('methods', 'Methods')}
          {tabBtn('containers', 'Containers')}
        </div>
        {!isAdmin && (
          <p className="text-xs text-neutral-500">A Nursery Admin seat sets these up. You can see them.</p>
        )}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        {tab === 'methods' && (
          <div className="space-y-4">
            {isAdmin && draft == null && (
              <Button variant="primary" size="sm" onClick={() => { setSaveError(null); setDraft(draftFrom(null)); }}>
                <Plus className="h-4 w-4 mr-1" /> New method
              </Button>
            )}
            {draft && (
              <MethodEditor draft={draft} onChange={setDraft} onCancel={() => setDraft(null)}
                onSave={saveMethod} saving={saving} error={saveError} />
            )}
            {methods == null ? (
              <div className="h-24 bg-neutral-100 rounded-xl animate-pulse" />
            ) : methods.length === 0 ? (
              <div className="bg-white border border-dashed border-neutral-300 rounded-xl px-6 py-10 text-center">
                <p className="text-sm text-neutral-600">No propagation methods yet.</p>
                <p className="text-xs text-neutral-400 mt-1">
                  Add how you propagate: seed, cuttings, tissue culture, layers, budding, grafting, or plants bought in.
                </p>
              </div>
            ) : (
              methods.map((m) => (
                <MethodCard key={m.id} method={m} canEdit={isAdmin}
                  onEdit={() => { setSaveError(null); setDraft(draftFrom(m)); }}
                  onToggle={() => toggleMethod(m)} />
              ))
            )}
          </div>
        )}

        {tab === 'containers' && <ContainerTypes items={containers} canEdit={isAdmin} reload={load} />}
      </div>
    </>
  );
}

export default PropagationSetup;
